/**
 * **Disposable fixture — Collectors.**
 *
 * Authored because the Network Collectors screens are being built ahead of their
 * backend: there is no endpoint, no DTO and no contract to derive rows from yet.
 * The numbers reproduce the design's frames (4 Collectors across 4 sites, 3
 * online, 12 Log Sources) so the screens can be reviewed against the mockups.
 *
 * **Delete condition:** the first real Collectors endpoint. When it lands, this
 * file and `log-sources.ts` are deleted and `../api.ts` calls the endpoint —
 * nothing else in the sub-feature should need to change.
 *
 * **Not demo-world content** (ADR 0017): the demo build authors its own data
 * from the real contract and must never import this. Nothing here is shaped for
 * a demo narrative.
 *
 * Ids are **UUIDs**, unreadable on purpose. A readable id (`col-hq-detroit`)
 * lands in the URL, and the breadcrumb derives its trail from the pathname while
 * dropping only segments that parse as a UUID — so a friendly id renders as a
 * breadcrumb crumb reading `Col Hq Detroit`. The real ids will be UUIDs; these
 * are what make the prototype's URLs behave like the real ones.
 *
 * `logVolume24h` is an authored **string**, not a byte count — see the field's
 * doc on `Collector`. It is on the backend's list of numbers nothing can supply
 * yet, so each row's figure is only required to agree with the source statuses
 * beneath it on that Collector's detail page.
 *
 * `lastSeenSecondsAgo` rather than a timestamp, because an absolute date
 * authored today reads as "3 days ago" next week. `../api.ts` resolves the
 * offset against the clock at fetch time, which also makes the page's refresh
 * behave like a real one.
 */
import type { Collector } from '../types'

export type CollectorFixture = Omit<Collector, 'lastSeenAt'> & {
	/** Seconds since the last heartbeat, resolved to `lastSeenAt` on fetch. */
	lastSeenSecondsAgo: number
}

/**
 * The Collector the Add Collector wizard hands off to.
 *
 * The wizard creates nothing (D2), so "finish" has to land on a Collector that
 * already exists — and the wizard prefills its name field with this one, so the
 * install command, the heartbeat line and the page you arrive on all name the
 * same Collector. Pulled out as a named const rather than indexed out of the
 * array below, because the array's order is the *page's* order (D35) and a
 * `COLLECTOR_FIXTURES[1]` would silently re-point the hand-off the day a row
 * moves.
 *
 * This one, of the four, because it is `connected` with the freshest heartbeat:
 * arriving on an offline or degraded Collector immediately after "Heartbeat
 * received" would contradict the sentence that sent you there.
 */
export const HANDOFF_COLLECTOR_FIXTURE: CollectorFixture = {
	id: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a802',
	name: 'Branch OKC, OK',
	site: 'Oklahoma City, OK',
	status: 'connected',
	lastSeenSecondsAgo: 3,
	ipAddress: '10.44.3.10',
	agentVersion: 'v1.4.2',
	logVolume24h: '840 MB',
}

export const COLLECTOR_FIXTURES: CollectorFixture[] = [
	{
		id: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a801',
		name: 'HQ — Detroit, MI',
		site: 'Detroit, MI',
		status: 'connected',
		lastSeenSecondsAgo: 0,
		ipAddress: '10.44.1.10',
		agentVersion: 'v1.4.2',
		logVolume24h: '2.1 GB',
	},
	HANDOFF_COLLECTOR_FIXTURE,
	{
		id: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a803',
		name: 'Branch — Austin, TX',
		site: 'Austin, TX',
		status: 'degraded',
		lastSeenSecondsAgo: 120,
		ipAddress: '10.44.4.10',
		agentVersion: 'v1.3.9',
		logVolume24h: '310 MB',
	},
	{
		id: '6b1f0c2a-3d4e-4a71-9c58-0f2b7d51a804',
		name: 'St. Johns, MI',
		site: 'St. Johns, MI',
		status: 'offline',
		lastSeenSecondsAgo: 840,
		ipAddress: '10.44.2.11',
		agentVersion: 'v1.4.2',
		// `0 B`, not the design frame's `2.1 GB`: this agent is offline and every
		// source on it is silent or awaiting its first log, so a 24h volume would
		// contradict the three rows underneath it on its own detail page.
		logVolume24h: '0 B',
	},
]
